import React, { useState } from 'react';
import { tokens } from '@companycam/slab-tokens';
import { Button, Modal, Notice, Spinner } from '@companycam/slab-web';
import EliteTierTrialModalBackground from 'images/EliteTierPricingModalBackground.png';

import styled from 'styled-components';
import useGetDefaultSampleDetails from './hooks/useGetDefaultSampleDetails';
import Timeline from 'components/billing/v2/paymentMethods/Timeline';
import { translate } from 'javascripts/translations/i18n';
import { formatDate } from 'helpers/intl-helper';
import { addDays } from 'date-fns';
import { useStartSample } from 'components/billing/v2/api/useStartSample';
import toast from 'helpers/toast';
import { type ClientSubscriptionOption } from 'components/billing/v2/types';
import useDetailedBillingQuery from 'components/billing/v2/useDetailedBillingQuery';
import { Steven } from 'helpers/steven';

type SampleModalProps = {
  onRequestClose: () => void;
};

const EliteCard = styled.div`
  border: 3px solid #000;
  border-radius: var(--cc_size_border_radius_xl);
  background-color: var(--cc_color_background_1);
  padding: var(--cc_size_spacing_xl);
  margin-left: var(--cc_size_spacing_xl);
  width: 70%;
`;

const ModalContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  min-width: 831px;
  min-height: 735px;

  @media (max-width: 850px) {
    min-width: 100%;
  }
`;

const RightColumn = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  padding: var(--cc_size_spacing_s);

  @media (max-width: 850px) {
    margin-left: var(--cc_size_spacing_xxl);
  }
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: var(--cc_size_spacing_l);
  right: var(--cc_size_spacing_xl);
`;

const CloseButtonIcon = styled.i`
  font-size: var(--cc_size_text_xl);
`;

const EliteCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  background-image: url(${EliteTierTrialModalBackground});
  background-size: contain;
  background-repeat: no-repeat;

  @media (max-width: 850px) {
    display: none;
  }
`;

const Title = styled.h1`
  font-family: 'Averta', sans-serif;
  line-height: 22.4px;
  font-size: var(--cc_size_text_l);
  font-weight: 600;
  color: var(--cc_color_text_1);
  padding-bottom: var(--cc_size_spacing_xl);
`;

const DollarsPerCycle = styled.h2`
  font-family: 'Averta', sans-serif;
  line-height: 22.4px;
  font-size: var(--cc_size_text_xl);
  font-weight: 600;
  color: var(--cc_color_text_1);
  margin-bottom: var(--cc_size_spacing_l);
`;

const EliteCardText = styled.p<{ $isBold?: boolean }>`
  font-family: Averta, sans-serif;
  font-size: var(--cc_size_text_s);
  line-height: 22.4px;
  font-weight: ${({ $isBold }) => ($isBold ? 700 : 400)};
`;

const CurrencyAmount = styled.span`
  font-family: Averta, sans-serif;
  font-size: 48px;
`;

const CancelInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: var(--cc_size_spacing_s);
`;

const CancelHeadline = styled.p`
  font-family: Averta, sans-serif;
  font-size: var(--cc_size_text_m);
  color: var(--cc_color_text_default_le);
  font-weight: 600;
`;

const CancelInfoText = styled.p`
  font-family: Averta, sans-serif;
  font-size: var(--cc_size_text_s);
  color: var(--cc_color_text_default_le);
  line-height: var(--cc_size_text_l);
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--cc_size_spacing_s);
  margin-top: var(--cc_size_spacing_xl);
`;

const StyledHeader = styled.h1`
  font-family: Averta, sans-serif;
  font-size: var(--cc_size_text_xxl);
  width: 80%;

  @media (max-width: 850px) {
    width: 100%;
  }
`;

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--cc_size_spacing_xs);
`;

const TimelineContainer = styled.div`
  margin-top: var(--cc_size_spacing_xl);
  margin-bottom: var(--cc_size_spacing_xs);
`;

const determinePrice = (isAnnual: boolean, price: string) => {
  const parsedPrice = parseInt(price, 10);
  return isAnnual && !Number.isNaN(parsedPrice)
    ? parsedPrice / 12
    : parsedPrice;
};

const SampleModal = ({ onRequestClose }: SampleModalProps) => {
  const { data, isLoading: loading } = useGetDefaultSampleDetails();
  const sampleDetails = data?.data;
  const isAnnual = sampleDetails?.cycle === 'annual';
  const { availableBillingPlans } = useDetailedBillingQuery();
  const [activatingTrial, setActivatingTrial] = useState(false);

  const navigateToBillingPlans = () => {
    Steven.elementClicked({
      id: 'elite_trial_modal',
      content: 'billing.v2.sample.modal.seeAllPlansAndOffers',
      action: 'sample_modal_button_clicked',
      type: 'button',
    });
    onRequestClose();
    window.location.href = '/billing/plans';
  };

  const startSampleApi = useStartSample({
    onSuccess: () => {
      onRequestClose();
      toast.success(translate('billing.comparePlans.activateSampleSuccess'));
      setTimeout(() => window.location.reload(), 2000);
    },
    onError: () => {
      setActivatingTrial(false);
    },
  });

  const activateTrial = () => {
    Steven.elementClicked({
      id: 'elite_trial_modal',
      content: 'billing.v2.sample.modal.tryFreeFor14Days',
      action: 'sample_modal_button_clicked',
      type: 'button',
    });
    setActivatingTrial(true);
    const elitePlan = availableBillingPlans.find(
      (plan: ClientSubscriptionOption) => plan.identifier === 'elite_plan',
    );

    setTimeout(() => {
      startSampleApi.mutate({
        productId: elitePlan.productId,
      });
    }, 500);
  };

  return (
    <Modal
      isOpen
      onClose={onRequestClose}
      title=""
      bodyStyle={{
        padding: `0 ${tokens.size_spacing_xl}px 0 0`,
        borderRadius: tokens.size_border_radius_l,
        backgroundColor: 'var(--cc_color_background_2)',
      }}
      footerComponent={null}
      headerComponent={null}
      data-testid="sampleModal__elite_sample_modal"
    >
      <CloseButtonWrapper>
        <CloseButtonIcon
          className="mdi mdi-close"
          onClick={onRequestClose}
          aria-label={translate('common.close')}
        />
      </CloseButtonWrapper>
      <ModalContainer>
        <EliteCardContainer>
          {loading ? null : (
            <EliteCard>
              <Title>{sampleDetails?.name}</Title>
              <DollarsPerCycle>
                <CurrencyAmount>{`$${determinePrice(
                  isAnnual,
                  sampleDetails?.baseMonthlyPrice,
                )}`}</CurrencyAmount>
                {translate('billing.card.perMonth')}
              </DollarsPerCycle>
              <EliteCardText $isBold>
                {translate('billing.v2.sample.userIncludes', {
                  userCount: sampleDetails?.minSeats,
                })}
              </EliteCardText>
              <EliteCardText>
                {translate('billing.card.userPricing', {
                  userPrice: determinePrice(
                    isAnnual,
                    sampleDetails?.pricePerUser,
                  ),
                })}
              </EliteCardText>
              <EliteCardText>
                {isAnnual
                  ? translate('billing.card.yearlyTag')
                  : translate('billing.card.monthlyTag')}
              </EliteCardText>
            </EliteCard>
          )}
        </EliteCardContainer>
        <RightColumn>
          <StyledHeader>
            {translate('billing.v2.sample.modal.title')}
          </StyledHeader>
          <TimelineContainer>
            <Timeline
              timelineSubHeaderOne={translate(
                'billing.v2.sample.modal.todaySubtext',
              )}
              timelineSubHeaderTwo={translate(
                'billing.v2.sample.modal.day12Subtext',
              )}
              timelineSubHeaderThree={translate(
                'billing.v2.sample.modal.day14Subtext',
                {
                  monthAndDay: formatDate(addDays(new Date(), 14), 'MMMM do'),
                },
              )}
            />
          </TimelineContainer>

          <CancelInfoContainer>
            <CancelHeadline>
              {translate('billing.v2.sample.modal.cancelAnytime')}
            </CancelHeadline>
            <CancelInfoText>
              {translate('billing.v2.sample.modal.cancelAnytimeSubtext')}
            </CancelInfoText>
          </CancelInfoContainer>

          {startSampleApi.isError ? (
            <Notice ccMargin="m 0 0 0" color="destroy">
              {translate('billing.v2.sample.modal.errorMessage')}
            </Notice>
          ) : null}

          <ButtonWrapper>
            <Button
              disabled={activatingTrial}
              size="large"
              color="primary"
              onClick={activateTrial}
              data-testid="sampleModal__activate_trial"
            >
              <ButtonContent>
                {activatingTrial ? (
                  <Spinner
                    size="xsmall"
                    accessibilityLabel={translate('common.loading')}
                  />
                ) : null}
                {translate('billing.v2.sample.modal.tryFreeFor14Days')}
              </ButtonContent>
            </Button>
            <Button
              disabled={activatingTrial}
              size="large"
              color="subtle"
              onClick={navigateToBillingPlans}
              data-testid="sampleModal__see_all_plans_and_offers"
            >
              {translate('billing.v2.sample.modal.seeAllPlansAndOffers')}
            </Button>
          </ButtonWrapper>
        </RightColumn>
      </ModalContainer>
    </Modal>
  );
};

export default SampleModal;
